import { ChatUiVariants } from '@trimble-agentic-external-npm-local/agentic-platform-sdk-iframe-typescript';

import { trackEvent } from './analytics';
import type { ExtendedChatUiConfiguration } from '../providers/embedded-config-provider';

export const EMBEDDED_ANALYTICS_FEATURE = 'embedded';

export type EmbeddedVariant = 'full' | 'minimal' | 'narrow';

type EmbeddedTrackedChatEvent = {
  type: string;
  payload?: unknown;
};

function asNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function chatEventPayload(payload: unknown): {
  agentId?: string;
  status?: 'completed' | 'failed' | 'cancelled';
} {
  if (!payload || typeof payload !== 'object') {
    return {};
  }

  const record = payload as Record<string, unknown>;
  const status = record.status;
  return {
    agentId: asNonEmptyString(record.agentId),
    status:
      status === 'completed' || status === 'failed' || status === 'cancelled'
        ? status
        : undefined,
  };
}

let openedThisLoad = false;

export function resolveEmbeddedVariant(
  variant: string | undefined
): EmbeddedVariant {
  if (variant === 'narrow') {
    return 'narrow';
  }
  if (variant === ChatUiVariants.Minimal) {
    return 'minimal';
  }
  return 'full';
}

export function embeddedEventParams(
  variant: EmbeddedVariant,
  entityId?: string,
  extra?: Record<string, string | number | boolean | undefined>
) {
  return {
    feature: EMBEDDED_ANALYTICS_FEATURE,
    variant,
    entity_type: entityId ? 'agent' : undefined,
    entity_id: entityId || undefined,
    ...extra,
  };
}

function contentVariantParam(value: unknown): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (value === 0 || value === 'Chat' || value === 'chat') {
    return 'chat';
  }
  if (value === 1 || value === 'AgentCards' || value === 'agent_cards') {
    return 'agent_cards';
  }
  return String(value);
}

function enabledFeatureFlags(features: unknown): string | undefined {
  if (!features || typeof features !== 'object') {
    return undefined;
  }

  const enabled = Object.entries(features as Record<string, unknown>)
    .filter(([, value]) => value === true)
    .map(([key]) => key)
    .sort();

  return enabled.length > 0 ? enabled.join(',') : undefined;
}

function parentOrigin(): string | undefined {
  try {
    return asNonEmptyString(document.location.ancestorOrigins?.[0]);
  } catch {
    return undefined;
  }
}

function openedConfigParams(chatConfig: ExtendedChatUiConfiguration) {
  const variant = resolveEmbeddedVariant(chatConfig.uiConfig.variant);
  const buttons = chatConfig.uiConfig.chatInput?.buttons;

  return embeddedEventParams(variant, chatConfig.agentId, {
    theme: asNonEmptyString(chatConfig.uiConfig.theme),
    environment: asNonEmptyString(chatConfig.environment),
    locale: asNonEmptyString(chatConfig.localization?.locale),
    language: asNonEmptyString(chatConfig.localization?.selectedLanguage),
    content_variant: contentVariantParam(chatConfig.uiConfig.contentVariant),
    hide_model_selection: chatConfig.uiConfig.chatInput?.hideModelSelection,
    show_sign_in: chatConfig.uiConfig.showSignIn,
    show_close_button: chatConfig.uiConfig.showCloseButton,
    has_thread: Boolean(chatConfig.threadId),
    has_mcp_app: chatConfig.mcpAppConfig !== undefined,
    chat_input_button_count: Array.isArray(buttons) ? buttons.length : 0,
    product_agent_count: chatConfig.uiConfig.productAgents?.length ?? 0,
    feature_flags: enabledFeatureFlags(chatConfig.features),
    parent_origin: parentOrigin(),
  });
}

/**
 * Fires `embedded_opened` once per iframe document load after a variant mounts.
 * Includes the init configuration hosts used so product can measure embed usage.
 */
export function trackEmbeddedOpenedOnce(
  chatConfig: ExtendedChatUiConfiguration
): void {
  if (openedThisLoad) {
    return;
  }

  openedThisLoad = true;
  trackEvent('embedded_opened', openedConfigParams(chatConfig));
}

/**
 * Maps chat-lib lifecycle events to GA4. Host `sendChatEvent` forwarding stays
 * in the chat components so the iframe contract is unchanged.
 */
export function trackEmbeddedChatEvent(
  variant: EmbeddedVariant,
  event: EmbeddedTrackedChatEvent,
  fallbackAgentId?: string
): void {
  const payload = chatEventPayload(event.payload);
  const agentId = payload.agentId || fallbackAgentId;

  if (event.type === 'run:started') {
    trackEvent('embedded_message_sent', embeddedEventParams(variant, agentId));
    return;
  }

  if (event.type !== 'run:ended') {
    return;
  }

  if (payload.status === 'completed') {
    trackEvent(
      'embedded_response_received',
      embeddedEventParams(variant, agentId, { result: 'success' })
    );
    return;
  }

  if (payload.status === 'failed') {
    trackEvent(
      'embedded_response_failed',
      embeddedEventParams(variant, agentId, { result: 'error' })
    );
  }
}

export function trackEmbeddedAgentSelected(
  variant: EmbeddedVariant,
  agentId?: string
): void {
  trackEvent('embedded_agent_selected', embeddedEventParams(variant, agentId));
}

export function trackEmbeddedNewChat(
  variant: EmbeddedVariant,
  agentId?: string
): void {
  trackEvent('embedded_new_chat', embeddedEventParams(variant, agentId));
}

/** @internal */
export function resetEmbeddedOpenedForTests(): void {
  openedThisLoad = false;
}
